import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Navigate, useLocation } from "react-router-dom";

import { api, formatApiError } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | anon | authed
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data.user);
      setMemberships(res.data.memberships || []);
      setStatus("authed");
      setError(null);
    } catch (err) {
      setUser(null);
      setMemberships([]);
      setStatus("anon");
    }
  }, []);

  useEffect(() => {
    // Emergent Google Auth · If we're returning from the OAuth callback,
    // let the callback page exchange the session_id and set the cookie
    // BEFORE we try `/auth/me` — otherwise the /me check races the
    // session issue and forces a login redirect.
    if (
      typeof window !== "undefined" &&
      window.location?.hash?.includes("session_id=") &&
      window.location?.pathname?.includes("/brand-os/auth/callback")
    ) {
      setStatus("loading");
      return;
    }
    refresh();
  }, [refresh]);

  const login = useCallback(
    async (email, password) => {
      setError(null);
      try {
        const res = await api.post("/auth/login", { email, password });
        setUser(res.data.user);
        setMemberships(res.data.memberships || []);
        setStatus("authed");
        return { ok: true };
      } catch (err) {
        const msg = formatApiError(err);
        setError(msg);
        return { ok: false, error: msg };
      }
    },
    []
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {
      /* ignore */
    }
    setUser(null);
    setMemberships([]);
    setStatus("anon");
  }, []);

  const activeMembership = memberships[0] || null;
  const role = activeMembership?.role || null;

  const value = useMemo(
    () => ({
      user,
      memberships,
      activeMembership,
      role,
      status,
      error,
      login,
      logout,
      refresh,
    }),
    [user, memberships, activeMembership, role, status, error, login, logout, refresh]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

export function RequireAuth({ children }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") {
    return (
      <div className="bo-root" data-testid="auth-loading">
        <div className="bo-login-shell">
          <div className="bo-login-card" style={{ textAlign: "center" }}>
            <div className="bo-login-eyebrow">Precipice Brand OS</div>
            <p className="bo-login-lede" style={{ marginTop: 8 }}>Checking your session…</p>
          </div>
        </div>
      </div>
    );
  }
  if (status === "anon") {
    return (
      <Navigate
        to="/brand-os/login"
        replace
        state={{ from: location.pathname }}
      />
    );
  }
  return children;
}
